import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  TouchableWithoutFeedback,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { AntDesign, Feather } from '@expo/vector-icons';
import axios from 'axios';
import { colors } from '../../style/rulesEngine';
import { getAuthHeaders, getBaseUrl } from '../../helpers/rulesApiHelpers';
import { extractGtin14FromBarcode } from '../../helpers/gs1Barcode';
import useGreenScrollbar from './useGreenScrollbar';

const STATUS_COLORS = {
  active:   colors.accent,
  inactive: colors.textMuted,
  new:      colors.accent,
};

const PAGE_SIZE = 50;

const SCROLL_THRESHOLD = 80;

const mapProduct = (p) => ({
  id:         p.id,
  name:       p.name || p.product_name || '',
  sku:        p.sku  || p.primary_sku  || '',
  qtyOnHand:  p.qty_on_hand,
  status:     (p.status || '').toString().toLowerCase(),
  barcode:    p.barcode || '',
});

const buildPath = (baseUrl, term, pageOffset) => {
  const limit = PAGE_SIZE;
  const gtin14 = extractGtin14FromBarcode(term);
  const searchTerm = gtin14 || term;
  return searchTerm && searchTerm.trim()
    ? `${baseUrl}/products/search.json?search=${encodeURIComponent(searchTerm.trim())}&limit=${limit}&offset=${pageOffset}`
    : `${baseUrl}/products.json?limit=${limit}&offset=${pageOffset}`;
};

const SKUPickerModal = ({ visible, onClose, selectedSkus, onSKUsSelected }) => {
  const [products, setProducts]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [fetchError, setFetchError]     = useState(null);
  const [search, setSearch]             = useState('');
  const [offset, setOffset]             = useState(0);
  const [hasMore, setHasMore]           = useState(true);
  const [pendingSkus, setPendingSkus]   = useState(new Set());
  const fetchTokenRef = useRef(0);

  useGreenScrollbar('sku-picker-scroll');

  useEffect(() => {
    if (visible) {
      const initial = Array.isArray(selectedSkus) ? selectedSkus : (selectedSkus ? [selectedSkus] : []);
      setPendingSkus(new Set(initial));
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const token = ++fetchTokenRef.current;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setFetchError(null);
      setProducts([]);
      setOffset(0);
      setHasMore(true);
      try {
        const baseUrl = await getBaseUrl();
        const headers = await getAuthHeaders();
        const res = await axios.get(buildPath(baseUrl, search, 0), { headers });
        if (cancelled || token !== fetchTokenRef.current) return;
        const raw = res.data?.products || (Array.isArray(res.data) ? res.data : []);
        const list = raw.map(mapProduct);
        setProducts(list);
        setHasMore(list.length >= PAGE_SIZE);
      } catch (err) {
        if (cancelled || token !== fetchTokenRef.current) return;
        setFetchError('Could not load products. Please try again.');
        setHasMore(false);
      }
      if (!cancelled && token === fetchTokenRef.current) setLoading(false);
    }, search ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [visible, search]);

  useEffect(() => {
    if (visible) setSearch('');
  }, [visible]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const token = fetchTokenRef.current;
    const nextOffset = offset + PAGE_SIZE;
    setLoadingMore(true);
    try {
      const baseUrl = await getBaseUrl();
      const headers = await getAuthHeaders();
      const res = await axios.get(buildPath(baseUrl, search, nextOffset), { headers });
      if (token !== fetchTokenRef.current) return;
      const raw = res.data?.products || (Array.isArray(res.data) ? res.data : []);
      const list = raw.map(mapProduct);
      setProducts((prev) => [...prev, ...list]);
      setOffset(nextOffset);
      if (list.length < PAGE_SIZE) setHasMore(false);
    } catch (err) {
      setHasMore(false);
    } finally {
      if (token === fetchTokenRef.current) setLoadingMore(false);
    }
  }, [hasMore, loading, loadingMore, offset, search]);

  const handleScroll = useCallback((event) => {
    const ne = event?.nativeEvent;
    if (!ne) return;
    const layout    = ne.layoutMeasurement || {};
    const contentSz = ne.contentSize || {};
    const contentY  = ne.contentOffset?.y || 0;
    if (!layout.height || !contentSz.height) return;
    const distanceFromBottom = contentSz.height - (layout.height + contentY);
    if (distanceFromBottom < SCROLL_THRESHOLD) loadMore();
  }, [loadMore]);

  const toggleSku = (sku) => {
    if (!sku) return;
    setPendingSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) {
        next.delete(sku);
      } else {
        next.add(sku);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onSKUsSelected(Array.from(pendingSkus));
    onClose();
  };

  if (!visible) return null;

  const selectedCount = pendingSkus.size;
  const confirmLabel = selectedCount > 0
    ? `Confirm (${selectedCount} selected)`
    : 'Confirm';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} testID="sku-picker-modal">
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={modalStyles.backdrop}>
          <TouchableWithoutFeedback>
            <View style={modalStyles.container}>
              {/* ── Header ──────────────────────────────────────────── */}
              <View style={modalStyles.header}>
                <View style={modalStyles.titleRow}>
                  <Text style={modalStyles.title}>Search</Text>
                  <Feather name="search" size={18} color={colors.text} style={{ marginHorizontal: 8 }} />
                  <Text style={modalStyles.title}>to Add SKU</Text>
                </View>
                <TouchableOpacity onPress={onClose} testID="sku-picker-close">
                  <AntDesign name="close" size={20} color={colors.text} />
                </TouchableOpacity>
              </View>

              {/* ── Search input ────────────────────────────────────── */}
              <View style={modalStyles.searchRow}>
                <TextInput
                  style={modalStyles.searchInput}
                  value={search}
                  onChangeText={setSearch}
                  placeholder="Type SKU or product name to search"
                  placeholderTextColor={colors.textMuted}
                  testID="sku-picker-search"
                />
                <Feather name="search" size={16} color={colors.textMuted} style={modalStyles.searchIcon} />
              </View>

              {/* ── States ──────────────────────────────────────────── */}
              {loading && <ActivityIndicator style={{ marginVertical: 12 }} color={colors.accent} />}
              {fetchError && <Text style={modalStyles.errorText}>{fetchError}</Text>}

              {/* ── Table header ────────────────────────────────────── */}
              {!loading && !fetchError && products.length > 0 && (
                <View style={modalStyles.tableHeader}>
                  <View style={modalStyles.colSelect} />
                  <Text style={[modalStyles.headerCell, modalStyles.colSku]}>SKU</Text>
                  <Text style={[modalStyles.headerCell, modalStyles.colName]}>Product Name</Text>
                  <Text style={[modalStyles.headerCell, modalStyles.colQoh]}>QOH</Text>
                  <Text style={[modalStyles.headerCell, modalStyles.colStatus]}>Status</Text>
                  <Text style={[modalStyles.headerCell, modalStyles.colBarcode]}>Barcode</Text>
                </View>
              )}

              {/* ── Rows ─────────────────────────────────────────────── */}
              <ScrollView
                style={modalStyles.list}
                onScroll={handleScroll}
                scrollEventThrottle={200}
                testID="sku-picker-scroll"
              >
                {!loading && !fetchError && products.length === 0 && (
                  <Text style={modalStyles.emptyText}>No products found.</Text>
                )}
                {products.map((p) => {
                  const isChecked = pendingSkus.has(p.sku);
                  const statusColor = STATUS_COLORS[p.status] || colors.textMuted;
                  return (
                    <TouchableOpacity
                      key={p.id}
                      onPress={() => toggleSku(p.sku)}
                      style={[modalStyles.row, isChecked && modalStyles.rowSelected]}
                      testID={`sku-picker-row-${p.id}`}
                    >
                      <View style={modalStyles.colSelect}>
                        <View style={[modalStyles.checkboxOuter, isChecked && modalStyles.checkboxOuterSelected]}>
                          {isChecked && <Text style={modalStyles.checkmark}>✓</Text>}
                        </View>
                      </View>
                      <Text style={[modalStyles.cell, modalStyles.colSku]} numberOfLines={1}>
                        {p.sku || '—'}
                      </Text>
                      <Text style={[modalStyles.cell, modalStyles.colName]} numberOfLines={1}>
                        {p.name || '—'}
                      </Text>
                      <Text style={[modalStyles.cell, modalStyles.colQoh]} numberOfLines={1}>
                        {p.qtyOnHand != null ? String(p.qtyOnHand) : '—'}
                      </Text>
                      <Text style={[modalStyles.cell, modalStyles.colStatus, { color: statusColor }]} numberOfLines={1}>
                        {p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1) : '—'}
                      </Text>
                      <Text style={[modalStyles.cell, modalStyles.colBarcode]} numberOfLines={1}>
                        {p.barcode || '—'}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
                {loadingMore && (
                  <ActivityIndicator
                    style={{ marginVertical: 12 }}
                    color={colors.accent}
                    testID="sku-picker-load-more"
                  />
                )}
              </ScrollView>

              {/* ── Confirm footer ───────────────────────────────────── */}
              <View style={modalStyles.footer}>
                <TouchableOpacity
                  onPress={handleConfirm}
                  disabled={selectedCount === 0}
                  style={[modalStyles.confirmButton, selectedCount === 0 && modalStyles.confirmButtonDisabled]}
                  testID="sku-picker-confirm"
                >
                  <Text style={[modalStyles.confirmText, selectedCount === 0 && modalStyles.confirmTextDisabled]}>
                    {confirmLabel}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

const modalStyles = {
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  container: {
    width: '100%',
    maxWidth: 960,
    maxHeight: '80%',
    backgroundColor: colors.cardBg,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  searchRow: {
    position: 'relative',
    marginBottom: 14,
  },
  searchInput: {
    backgroundColor: colors.inputBg,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    paddingVertical: 10,
    paddingLeft: 12,
    paddingRight: 36,
    color: colors.text,
    fontSize: 14,
  },
  searchIcon: {
    position: 'absolute',
    right: 12,
    top: 12,
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerCell: {
    color: colors.textMuted,
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  list: {
    flexGrow: 0,
    maxHeight: 380,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  rowSelected: {
    backgroundColor: 'rgba(28,187,119,0.12)',
  },
  cell: {
    color: colors.text,
    fontSize: 13,
  },
  colSelect:  { width: 32, alignItems: 'center', justifyContent: 'center' },
  colSku:     { flex: 1, paddingRight: 8 },
  colName:    { flex: 2.5, paddingRight: 8 },
  colQoh:     { flex: 0.8, paddingRight: 8, textAlign: 'left' },
  colStatus:  { flex: 1, paddingRight: 8 },
  colBarcode: { flex: 1.2, paddingRight: 8 },
  checkboxOuter: {
    width: 18,
    height: 18,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: colors.textMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxOuterSelected: {
    borderColor: colors.accent,
    backgroundColor: colors.accent,
  },
  checkmark: {
    color: '#fff',
    fontSize: 12,
    lineHeight: 14,
    fontWeight: '700',
  },
  emptyText: {
    paddingVertical: 16,
    color: colors.textMuted,
    textAlign: 'center',
  },
  errorText: {
    color: colors.danger,
    marginVertical: 8,
    textAlign: 'center',
  },
  footer: {
    marginTop: 12,
    alignItems: 'flex-end',
  },
  confirmButton: {
    backgroundColor: colors.accent,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  confirmButtonDisabled: {
    backgroundColor: colors.border,
  },
  confirmText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  confirmTextDisabled: {
    color: colors.textMuted,
  },
};

export default SKUPickerModal;
